import React from 'react';

import { WarningOutlined } from '@ant-design/icons';
import { Card, Table, Tag, Alert, Empty } from 'antd';
import { useTranslation } from 'react-i18next';

const RISK_CONFIG = {
  critical: { color: 'red', sortOrder: 0 },
  warning: { color: 'orange', sortOrder: 1 },
  healthy: { color: 'green', sortOrder: 2 },
  dead_stock: { color: 'default', sortOrder: 3 },
};

/**
 * InventoryHealthTable - 庫存健康度表格
 *
 * @param {Object} props
 * @param {Object} props.inventoryHealth - 庫存健康度資料
 * @param {Array}  props.inventoryHealth.products - 商品庫存健康度列表
 * @param {Object} props.inventoryHealth.summary - 摘要 { stockout_risk_count, dead_stock_count }
 */
const InventoryHealthTable = ({ inventoryHealth }) => {
  const { t } = useTranslation();

  if (!inventoryHealth || !inventoryHealth.products || inventoryHealth.products.length === 0) {
    return (
      <Card title={t('shared.analytics.inventory_health.title')} size="small">
        <Empty description={t('common.empty.no_data')} />
      </Card>
    );
  }

  const { products, summary } = inventoryHealth;

  const columns = [
    {
      title: t('shared.analytics.inventory_health.sku'),
      dataIndex: 'sku',
      key: 'sku',
      width: 150,
    },
    {
      title: t('shared.analytics.inventory_health.product_name'),
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: t('shared.analytics.inventory_health.available_stock'),
      dataIndex: 'available_stock',
      key: 'available_stock',
      width: 120,
      sorter: (a, b) => a.available_stock - b.available_stock,
      render: (value) => (value || 0).toLocaleString(),
    },
    {
      title: t('shared.analytics.inventory_health.avg_daily_sales'),
      dataIndex: 'avg_daily_sales',
      key: 'avg_daily_sales',
      width: 120,
      sorter: (a, b) => a.avg_daily_sales - b.avg_daily_sales,
      render: (value) => (value || 0).toFixed(1),
    },
    {
      title: t('shared.analytics.inventory_health.days_of_stock'),
      dataIndex: 'days_of_stock',
      key: 'days_of_stock',
      width: 120,
      sorter: (a, b) => (a.days_of_stock ?? 9999) - (b.days_of_stock ?? 9999),
      render: (value) => (value != null ? value.toFixed(0) : '-'),
    },
    {
      title: t('shared.analytics.inventory_health.status'),
      dataIndex: 'stockout_risk',
      key: 'stockout_risk',
      width: 120,
      sorter: (a, b) =>
        (RISK_CONFIG[a.stockout_risk]?.sortOrder ?? 99) - (RISK_CONFIG[b.stockout_risk]?.sortOrder ?? 99),
      render: (risk) => {
        const config = RISK_CONFIG[risk] || { color: 'default' };
        return (
          <Tag color={config.color}>
            {t(`shared.analytics.inventory_health.risk_${risk}`)}
          </Tag>
        );
      },
    },
  ];

  const hasAlerts = summary.stockout_risk_count > 0 || summary.dead_stock_count > 0;

  return (
    <Card title={t('shared.analytics.inventory_health.title')} size="small">
      {hasAlerts && (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message={t('shared.analytics.inventory_health.alert_summary', {
            stockout: summary.stockout_risk_count,
            dead: summary.dead_stock_count,
          })}
        />
      )}
      <Table
        dataSource={products}
        columns={columns}
        rowKey="product_id"
        pagination={false}
        size="small"
        scroll={{ x: 700 }}
      />
    </Card>
  );
};

export default InventoryHealthTable;
